/**
 * UI mirror of pricetooldb.suppliers: one entry per supplier key. The key is
 * the stem of the supply columns, the rebates store, the dtb<n>_supplier
 * value and the icon name.
 *
 * Every supplier has the same columns (<key>_price, <key>_qty,
 * <key>_in_stock, <key>_observed_at, <key>_source; migration
 * 20261030100000), so every supplier renders, edits and filters the same
 * way. DealsAreUs alone also has <key>_supplier_qty (what their own supplier
 * holds for them to order in), shown apart from the on-hand quantity.
 */
import allianceIcon from "../../../assets/icons/alliance.png";
import amazonIcon from "../../../assets/icons/amazon.png";
import ccvideoIcon from "../../../assets/icons/ccvideo.png";
import dealsareusIcon from "../../../assets/icons/dealsareus.png";
import deepdiscountIcon from "../../../assets/icons/deepdiscount.png";
import discountEntertainmentIcon from "../../../assets/icons/discount_entertainment.png";
import popmarketIcon from "../../../assets/icons/popmarket.png";
import collectorsChoiceIcon from "../../../assets/icons/collectors_choice.png";
import heartlandIcon from "../../../assets/icons/heartland.png";
import milaIcon from "../../../assets/icons/mila.png";
import gruvIcon from "../../../assets/icons/gruv.png";
import wildcard1Icon from "../../../assets/icons/wildcard1.png";
import wildcard2Icon from "../../../assets/icons/wildcard2.png";
import rarewavesIcon from "../../../assets/icons/rarewaves.png";
import targetIcon from "../../../assets/icons/target.png";
import veiIcon from "../../../assets/icons/vei.png";
import walmartIcon from "../../../assets/icons/walmart.png";
import wowhdIcon from "../../../assets/icons/wowhd.png";

export interface SupplierDef {
  key: string;
  name: string;
  shortCode: string;
  icon: string;
  /** Also has <key>_supplier_qty: quantity at the supplier's own supplier. */
  supplierQty?: boolean;
  /** Loaded through the Supplier CSV upload (can be cleared from the UI). */
  csv?: boolean;
  /** Website / supplier has ceased operations. */
  inactive?: boolean;
  priceDescription: string;
}

export const SUPPLIERS: readonly SupplierDef[] = [
  {
    key: "alliance",
    name: "Alliance",
    shortCode: "OMKT",
    icon: allianceIcon,
    priceDescription:
      "Alliance Entertainment distributor price (daily FTP feed)",
  },
  {
    key: "dealsareus",
    name: "DealsAreUs",
    shortCode: "DAUS",
    icon: dealsareusIcon,
    supplierQty: true,
    priceDescription: "DealsAreUs distributor price (daily FTP feed)",
  },
  {
    key: "amazon",
    name: "Amazon",
    shortCode: "AMZZ",
    icon: amazonIcon,
    priceDescription: "Amazon supplier scraped price",
  },
  {
    key: "deepdiscount",
    name: "Deep Discount",
    shortCode: "DPDS",
    icon: deepdiscountIcon,
    priceDescription: "Deep Discount supplier scraped price",
  },
  {
    key: "wowhd",
    name: "WOWHD",
    shortCode: "WOWD",
    icon: wowhdIcon,
    priceDescription: "WowHD supplier scraped price",
  },
  {
    key: "ccvideo",
    name: "CC Video",
    shortCode: "CCVD",
    icon: ccvideoIcon,
    priceDescription: "CC Video supplier scraped price",
  },
  {
    key: "walmart",
    name: "Walmart",
    shortCode: "WLMT",
    icon: walmartIcon,
    priceDescription: "Walmart supplier scraped price",
  },
  {
    key: "rarewaves",
    name: "Rarewaves",
    shortCode: "RRWV",
    icon: rarewavesIcon,
    priceDescription: "Rarewaves supplier scraped price",
  },
  {
    key: "popmarket",
    name: "Popmarket",
    shortCode: "POPM",
    icon: popmarketIcon,
    priceDescription: "Popmarket (formerly Ebay BlowItOutofHere) scraped price",
  },
  {
    key: "collectors_choice",
    name: "Collectors Choice",
    shortCode: "CCHO",
    icon: collectorsChoiceIcon,
    priceDescription: "Collectors Choice supplier price",
  },
  {
    key: "mila",
    name: "Mila",
    shortCode: "MILA",
    icon: milaIcon,
    csv: true,
    priceDescription: "Mila supplier price (CSV upload)",
  },
  {
    key: "discount_entertainment",
    name: "Discount Entertainment",
    shortCode: "DISC",
    icon: discountEntertainmentIcon,
    csv: true,
    priceDescription: "Discount Entertainment supplier price (CSV upload)",
  },
  {
    key: "wildcard1",
    name: "Wildcard1",
    shortCode: "WC1",
    icon: wildcard1Icon,
    csv: true,
    priceDescription: "Wildcard1 product price (CSV upload)",
  },
  {
    key: "wildcard2",
    name: "Wildcard2",
    shortCode: "WC2",
    icon: wildcard2Icon,
    csv: true,
    priceDescription: "Wildcard2 product price (CSV upload)",
  },
  {
    key: "gruv",
    name: "Gruv",
    shortCode: "GRUV",
    icon: gruvIcon,
    csv: true,
    inactive: true,
    priceDescription:
      "Gruv supplier price (Website / Supplier has ceased operations)",
  },
  {
    key: "inetvideo",
    name: "HeartLand",
    shortCode: "HEAR",
    icon: heartlandIcon,
    inactive: true,
    priceDescription:
      "InetVideo Music scraped price (Website / Supplier has ceased operations)",
  },
  {
    key: "target_store",
    name: "Target",
    shortCode: "TRGT",
    icon: targetIcon,
    inactive: true,
    priceDescription:
      "Target supplier scraped price (Website / Supplier has ceased operations)",
  },
  {
    key: "vei",
    name: "VEI",
    shortCode: "VVEI",
    icon: veiIcon,
    inactive: true,
    priceDescription:
      "VEI scraped price (Website / Supplier has ceased operations)",
  },
];

export const SUPPLIERS_BY_KEY: Readonly<Record<string, SupplierDef>> =
  Object.fromEntries(SUPPLIERS.map((s) => [s.key, s]));

/** Suppliers loaded through the CSV upload modal (and clearable). */
export const CSV_SUPPLIERS = SUPPLIERS.filter((s) => s.csv);
